import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting";
import * as am5data from "@amcharts/amcharts5";

interface MultiBarChartConfig {
  filepath: string;
  id: string;
  horizontal?: boolean;
  stacked?: boolean;
  percent?: boolean;
  colors?: string;
  dataString?: [];
}

type MultiBarChartData = Record<string, string | number>;
interface DataStringItem {
  Name: string;
  Value: string | number;
}

interface ChartWithId extends am5xy.XYChart {
  chart: any;
  id?: string;
  data?: MultiBarChartData[];
}

interface RootWithId extends am5.Root {
  root(root: any, arg1: { dataSource: any; filePrefix: string; }): unknown;
  id?: string;
}

declare const multibarchartarray: MultiBarChartConfig[];

const multiBarCharts: ChartWithId[] = [];
const multiBarChartRoots: RootWithId[] = [];

if (typeof multibarchartarray !== 'undefined') {
  for (const x of multibarchartarray) {
    am5.net.load(x.filepath).then((result) => {
      // Create root element
      // https://www.amcharts.com/docs/v5/getting-started/#Root_element
      const root = am5.Root.new(x.id) as RootWithId;

      let data: MultiBarChartData[] = [];

      if (x.filepath.includes(".csv")) {
        if (result.response) {

          const delimiter = result.response.includes(";") ? ";" : ",";
          data = am5data.CSVParser.parse(result.response, {
            delimiter,
            useColumnNames: true,
          });
        }
      } else if (x.dataString) {
        const row: MultiBarChartData = {};

        x.dataString.forEach((item: DataStringItem) => {
            row[item.Name] = parseFloat(item.Value as string);
        });

        data = [row];
      }

      if (data) {
        // Create root element
        // https://www.amcharts.com/docs/v5/getting-started/#Root_element
        root.setThemes([am5themes_Animated.new(root)]);


        const chart = root.container.children.push(am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: "panX",
          wheelY: "zoomX",
          layout: root.verticalLayout,
        })) as ChartWithId;

        if (x.colors) {
          const colorArray = x.colors.split(",").map(c => am5.color(c.trim()));
          chart.get("colors")?.set("colors", colorArray);
        }

        let xAxis: am5xy.ValueAxis<am5xy.AxisRenderer> | am5xy.CategoryAxis<am5xy.AxisRenderer>
        let yAxis: am5xy.ValueAxis<am5xy.AxisRenderer> | am5xy.CategoryAxis<am5xy.AxisRenderer>

        if (x.horizontal) {
          chart.set("scrollbarY", am5.Scrollbar.new(root, {
            orientation: "vertical",
            exportable: false,
          }));

          yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
            categoryField: Object.keys(data[0])[0],
            renderer: am5xy.AxisRendererY.new(root, {}),
            tooltip: am5.Tooltip.new(root, {}),
          }));
          yAxis.data.setAll(data);

          xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            ...(x.percent && x.stacked ? {
              max: 100,
              numberFormat: "#'%'",
              strictMinMax: true,
              calculateTotals: true,
            } : {}),
            renderer: am5xy.AxisRendererX.new(root, {}),
          }));
        } else {
          chart.set("scrollbarX", am5.Scrollbar.new(root, {
            orientation: "horizontal",
            exportable: false,
          }));

          xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
            categoryField: Object.keys(data[0])[0],
            renderer: am5xy.AxisRendererX.new(root, {}),
            tooltip: am5.Tooltip.new(root, {}),
          }));
          xAxis.data.setAll(data);

          yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
            min: 0,
            ...(x.percent && x.stacked ? {
              max: 100,
              numberFormat: "#'%'",
              strictMinMax: true,
              calculateTotals: true,
            } : {}),
            renderer: am5xy.AxisRendererY.new(root, {}),
          }));
        }

        const legend = chart.children.push(am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        }));

        const categoryField = Object.keys(data[0])[0];
        const seriesFields = Object.keys(data[0]).slice(1);

        for (const fieldName of seriesFields) {
          const displayName = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
          const series = chart.series.push(am5xy.ColumnSeries.new(root, {
            name: displayName,
            stacked: !!x.stacked,
            xAxis: xAxis as any,
            yAxis: yAxis as any,
            valueXField: x.horizontal ? fieldName : undefined,
            valueYField: x.horizontal ? undefined : fieldName,
            valueXShow: x.percent && x.stacked && x.horizontal ? "valueXTotalPercent" : "valueXWorking",
            valueYShow: x.percent && x.stacked && !x.horizontal ? "valueYTotalPercent" : "valueYWorking",
            categoryXField: x.horizontal ? undefined : categoryField,
            categoryYField: x.horizontal ? categoryField : undefined,
          }));

          series.columns.template.setAll({
            focusable: true,
            ariaLabel: x.horizontal ? "{categoryY}. {valueX}." : "{categoryX}. {valueY}.",
            role: "figure",
            tooltipText: x.percent && x.stacked && x.horizontal
              ? "{name}, {categoryY}: {valueXTotalPercent.formatNumber('#.#')}%"
              : x.percent && x.stacked && !x.horizontal
                ? "{name}, {categoryX}: {valueYTotalPercent.formatNumber('#.#')}%"
                : x.horizontal
                  ? "{name}, {categoryY}: {valueX}"
                  : "{name}, {categoryX}: {valueY}",
            tooltipY: x.horizontal ? am5.percent(90) : am5.percent(10),
          });

          series.data.processor = am5.DataProcessor.new(root, {
            numericFields: [fieldName],
          });

          series.data.setAll(data);

          const tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            autoTextColor: false,
            labelText: series.columns.template.get("tooltipText")!,
          });

          tooltip.get("background")?.setAll({ fill: am5.color("#696969") });

          tooltip.label.setAll({
            fill: am5.color(0xffffff)
          });

          series.set("tooltip", tooltip);

          series.bullets.push(() => am5.Bullet.new(root, {
            sprite: am5.Label.new(root, {
              text: x.percent && x.stacked && x.horizontal
                ? "{valueXTotalPercent.formatNumber('#.#')}%"
                : x.horizontal
                  ? "{valueX}"
                  : x.percent && x.stacked
                    ? "{valueYTotalPercent.formatNumber('#.#')}%"
                    : "{valueY}",
              fill: root.interfaceColors.get("alternativeText"),
              centerX: am5.p50,
              centerY: am5.p50,
              populateText: true,
            }),
          }));

          legend.data.push(series);
          series.appear();
        }

        if (am5xy.ValueAxis.isPrototypeOf(xAxis.constructor)) {
          (xAxis as am5xy.ValueAxis<any>).set("tooltip", am5.Tooltip.new(root, { themeTags: ["axis"] }));
        } else {
          (xAxis as am5xy.CategoryAxis<any>).set("tooltip", am5.Tooltip.new(root, { themeTags: ["axis"] }));
        }

        if (am5xy.ValueAxis.isPrototypeOf(yAxis.constructor)) {
          (yAxis as am5xy.ValueAxis<any>).set("tooltip", am5.Tooltip.new(root, { themeTags: ["axis"] }));
        } else {
          (yAxis as am5xy.CategoryAxis<any>).set("tooltip", am5.Tooltip.new(root, { themeTags: ["axis"] }));
        }

        chart.zoomOutButton.set("exportable", false);
        chart.appear(1000, 100);

        chart.id = x.id;
        chart.data = data;
        root.id = x.id;

        multiBarCharts.push(chart);
        multiBarChartRoots.push(root);
      }
    })
  }
}

export function exportMultiBarChart(format: "png" | "jpg" | "pdf" | "xlsx" | "csv" | "json" | "pdfdata", id: string) {

  const chart = multiBarCharts.find(c => c.id === id)
  const root = multiBarChartRoots.find(c => c.id === id)
  if (chart && root) {
    const exporting = am5plugins_exporting.Exporting.new(root, {
      dataSource: chart.data,
      filePrefix: "Stapeldiagram",
    });
    exporting.download(format);
  }
}


